import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const useFilters = ({
  initialSearch = "",
  initialCategory = "",
  initialSubCategory = "",
  initialChildCategory = "",
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL query parameters are the source of truth.
  const categoryFromUrl =
    searchParams.get("category") || initialCategory || "";

  const subcategoryFromUrl =
    searchParams.get("subcategory") || initialSubCategory || "";

  const childcategoryFromUrl =
    searchParams.get("childcategory") || initialChildCategory || "";

  const searchFromUrl =
    searchParams.get("search") || initialSearch || "";

  const [selectedCategory, setSelectedCategory] = useState(
    categoryFromUrl
  );

  const [selectedSubCategory, setSelectedSubCategory] = useState(
    subcategoryFromUrl
  );

  const [selectedChildCategory, setSelectedChildCategory] = useState(
    childcategoryFromUrl
  );

  const [priceRange, setPriceRange] = useState(() => {
    const range = searchParams.get("range");
    return range ? range.split(";").map(Number) : [0, 10000];
  });

  const [sortBy, setSortBy] = useState(
    searchParams.get("sort_by") || "Relevance"
  );

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const selectedColors = searchParams.getAll("color_code[]");

  // Keep local category state synchronized with browser URL navigation.
  useEffect(() => {
    setSelectedCategory(categoryFromUrl);
    setSelectedSubCategory(subcategoryFromUrl);
    setSelectedChildCategory(childcategoryFromUrl);
  }, [
    categoryFromUrl,
    subcategoryFromUrl,
    childcategoryFromUrl,
  ]);

  // Update non-category filters without deleting category selections.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (
          priceRange.length === 2 &&
          !(priceRange[0] === 0 && priceRange[1] === 10000)
        ) {
          next.set("range", `${priceRange[0]};${priceRange[1]}`);
        } else {
          next.delete("range");
        }

        if (sortBy !== "Relevance") {
          next.set("sort_by", sortBy);
        } else {
          next.delete("sort_by");
        }

        return next;
      },
      { replace: true }
    );
  }, [priceRange, sortBy, setSearchParams]);

  const queryParams = useMemo(() => {
    const params = {};

    if (categoryFromUrl) {
      params.category = categoryFromUrl;
    }

    if (subcategoryFromUrl) {
      params.subcategory = subcategoryFromUrl;
    }

   if (childcategoryFromUrl) {
  params.child_category = childcategoryFromUrl;
}

    if (searchFromUrl) {
      params.search = searchFromUrl;
    }

    if (
      priceRange.length === 2 &&
      !(priceRange[0] === 0 && priceRange[1] === 10000)
    ) {
      params.range = `${priceRange[0]};${priceRange[1]}`;
    }

    if (sortBy !== "Relevance") {
      params.sort_by = sortBy;
    }

    if (selectedColors.length > 0) {
      params["color_code[]"] = selectedColors;
    }

    return params;
  }, [
    categoryFromUrl,
    subcategoryFromUrl,
    childcategoryFromUrl,
    searchFromUrl,
    priceRange,
    sortBy,
    selectedColors.join(","),
  ]);

  const handlePageChange = (newPage) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(newPage));
        return next;
      },
      { replace: true }
    );
  };

  const handleResetFilters = () => {
    setSelectedSubCategory("");
    setSelectedChildCategory("");
    setPriceRange([0, 10000]);
    setSortBy("Relevance");

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("subcategory");
        next.delete("childcategory");
        next.delete("range");
        next.delete("sort_by");
        next.set("page", "1");
        return next;
      },
      { replace: true }
    );
  };

  return {
    filtersProps: {
      selectedCategory,
      selectedSubCategory,
      selectedChildCategory,
      priceRange,
      sortBy,
      mobileFiltersOpen,
    },

    filtersActions: {
      setSelectedCategory,
      setSelectedSubCategory,
      setSelectedChildCategory,
      setPriceRange,
      setSortBy,
      setMobileFiltersOpen,
      handleResetFilters,
      handlePageChange,
    },

    queryParams,
  };
};

export default useFilters;